import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { LoadReCaptchaScript } from '../../utils/recaptcha-utils';
import { isClient } from '../../utils/utils';
import { bffPost } from '../bff';
import type { GiftCardBalance } from './model/GiftCardCheckBalance';

export const useGiftCardCheckBalance = (): UseMutationResult<
    GiftCardBalance,
    unknown,
    GiftCardBalance
> => {
    const authContext = useAuthContext();
    const customPreferences = useCustomPreferences([
        CustomPreference.RECAPTCHA_SITEKEY,
        CustomPreference.RECAPTCHA_CHECKOUT_GC_ENABLED,
    ]);
    const recaptchaSiteKey = customPreferences[CustomPreference.RECAPTCHA_SITEKEY] as string;
    const recaptchaApplyGiftCardEnabled =
        !!customPreferences[CustomPreference.RECAPTCHA_CHECKOUT_GC_ENABLED];

    const getReCaptchaToken = LoadReCaptchaScript({
        siteKey: recaptchaSiteKey,
        enabled: recaptchaApplyGiftCardEnabled,
        action: 'giftCard',
    });

    return useMutation({
        mutationFn: async (body: GiftCardBalance) => {
            if (isClient) {
                await window.sfccSessionCreationPromise;
            }

            const { access_token } = await authContext.ready();
            const recaptchaToken = await getReCaptchaToken();

            return bffPost('/gift-card/check-balance', {
                accessToken: access_token,
                body,
                headers: {
                    'g-recaptcha-response': recaptchaToken || '',
                },
            });
        },
    });
};
